/**
 * @fileoverview My Page targets query hook.
 * @description Wraps GET /sales/targets (every page) in a TanStack query, keyed by the table's
 * filters, so each filter change reads its own cached result.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { SALES_CRM_QUERY_KEYS } from '@/features/sales/constants/prospects-query-keys.constants'
import { getAllMyPageTargets } from '@/features/sales/services/my-page-targets.service'

import type { MyPageTargetFilters } from '@/features/sales/types/my-page.types'

/**
 * @description Fetches the caller's My Page targets matching the filters, keeping the previous
 * result on screen while a changed filter loads.
 * @param {MyPageTargetFilters} filters - Search, stage, status, priority and include-closed.
 * @returns {ReturnType<typeof useQuery>} The query (data is every matching target).
 */
export function useMyPageTargets(filters: MyPageTargetFilters) {
  return useQuery({
    queryKey: SALES_CRM_QUERY_KEYS.myPageTargets(filters),
    queryFn: () => getAllMyPageTargets(filters),
    placeholderData: (previous) => previous,
  })
}
